import { i18n, i18nDynamic } from 'shared/i18n';
import authStore from 'shared/auth/authStore';
import { useGetDocument } from 'entities/document';
import { Modal, Spinner } from 'app/components';
import { useUser } from 'app/state/contexts';
import { useState, useEffect } from 'react';
import { useDocumentParams } from 'app/router/params';
import Tippy from '@tippyjs/react';
import { Clickable } from 'shared/ui/Clickable';
import { Level } from 'shared/ui/Level';
import { usePermissions } from 'app/api/permission-queries';
import { useAppliedThemeStatus, useCreateTheme, useDeleteTheme, useDemoteTheme, usePromoteTheme, useRenameTheme, useThemes } from 'entities/theme';
import { isSystemTheme, resolveAppliedTheme, sortThemes, ThemeSummary } from 'entities/theme';
import { ApplyThemeDialog } from '../ApplyThemeDialog';
import { ThemePreview } from '../design-configuration/components/ThemePreview';

const formatDate = (value?: string) => (value ? String(value).slice(0, 10) : '');

const SORT_TABS = ['created', 'name', 'type'] as const;
/** Each tab's natural reading: newest first, A–Ö, system before organisation. */
const SORT_DIRECTION_DEFAULTS: Record<string, string> = { created: 'desc', name: 'asc', type: 'desc' };

interface ThemeMenuPerms {
  system: boolean;
  canRename: boolean;
  canDelete: boolean;
  canPromote: boolean;
}

function ThemeNameModal({
  show,
  onClose,
  initialName = '',
  titleLabelKey,
  onSave,
}: {
  show: boolean;
  onClose: () => void;
  initialName?: string;
  titleLabelKey: 'theme:create-new';
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(initialName);

  useEffect(() => {
    setName(initialName);
  }, [initialName, show]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      return;
    }
    onSave(name.trim());
    onClose();
  };

  return (
    <Modal titleLabelKey={titleLabelKey} show={show} onClose={onClose}>
      <form onSubmit={handleSave}>
        <label className="form-label" htmlFor="input-theme-name">
          {i18n('theme:name')}
        </label>
        <input className="form-control" id="input-theme-name" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="d-flex justify-content-end mt-5">
          <button type="button" className="btn" onClick={onClose}>
            {i18n('common:button.cancel')}
          </button>
          <button className="btn btn-primary">{i18n('common:button.save')}</button>
        </div>
      </form>
    </Modal>
  );
}

export function ThemeSelection() {
  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const { data: pDocument, isSuccess } = useGetDocument(documentId, selectedOrganization);
  const { documentPermissions } = usePermissions(pDocument?.superId ?? documentId);
  const themes = useThemes(documentId);
  const appliedStatus = useAppliedThemeStatus(documentId);
  const { mutate: createTheme } = useCreateTheme();

  const [showSelectModal, setShowSelectModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [themeToApply, setThemeToApply] = useState<ThemeSummary | null>(null);

  if (!isSuccess) {
    return <Spinner />;
  }

  const applied = resolveAppliedTheme(pDocument?.content?.theme, themes.isSuccess ? themes.data : undefined, appliedStatus.data?.modified ?? null);
  const canOwnThemes = documentPermissions.includes('DESIGN') || documentPermissions.includes('CODER');

  return (
    <>
      <div className="d-flex flex-row justify-content-between align-items-center">
        <h2 className="m-0">{i18n('theme:latest-applied')}</h2>
        <div className="d-flex flex-row align-items-center gap-r-2">
          {canOwnThemes && (
            <Clickable onClick={() => setShowCreateModal(true)} className="d-flex flex-row align-items-center gap-r-0-5">
              <i className="fal fa-plus"></i>
              <span>{i18n('theme:create-new')}</span>
            </Clickable>
          )}
          <button
            onClick={() => setShowSelectModal(true)}
            type="button"
            className="btn-clear rounded-circle aspect-1x1 bg-green-700 text-white d-flex justify-content-center align-items-center"
            style={{ height: '43px', fontSize: '15px' }}
            aria-label={i18n('theme:select-theme')}
          >
            <i className="fa-regular fa-ellipsis-vertical fa-center-fix"></i>
          </button>
        </div>
      </div>

      <div className="bg-light border border-blue-300 rounded w-100 px-4 py-3 mt-2 d-flex flex-row align-items-center gap-r-1 flex-wrap">
        {applied.applied ? (
          <div className="d-flex flex-column w-100 gap-r-0-5">
            <div className="d-flex flex-row align-items-center">
              <span className="fw-medium">{applied.name}</span>
              <span className="ms-auto">
                <Level organization={applied.scope === 'system' ? null : 1} />
              </span>
            </div>
            <div className="d-flex flex-column text-muted fs-7">
              <span>{`${i18n('theme:applied-at')} ${formatDate(applied.appliedAt ?? undefined)}`}</span>
              <span>{`${i18n('theme:created-at')} ${formatDate(applied.createdAt ?? undefined)}`}</span>
            </div>
            {(applied.deleted || applied.modified === true) && (
              <div className="alert alert-info d-flex flex-row gap-r-0-5 align-items-start py-2 px-3 mb-0 mt-1 fs-7">
                <i className="fal fa-circle-info mt-1"></i>
                <span>{i18n(applied.deleted ? 'theme:deleted-suffix' : 'theme:modified-suffix')}</span>
              </div>
            )}
          </div>
        ) : (
          <span className="text-muted">{i18n('theme:none-applied')}</span>
        )}
      </div>

      <ThemeSelectionModal
        show={showSelectModal}
        onClose={() => setShowSelectModal(false)}
        documentPermissions={documentPermissions}
        onApply={(theme) => {
          setShowSelectModal(false);
          setThemeToApply(theme);
        }}
      />
      <ApplyThemeDialog show={!!themeToApply} onClose={() => setThemeToApply(null)} theme={themeToApply} documentSuperId={documentId} />
      <ThemeNameModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        titleLabelKey="theme:create-new"
        onSave={(name) => createTheme({ documentSuperId: documentId, name })}
      />
    </>
  );
}

function ThemeSelectionModal({
  show,
  onClose,
  documentPermissions,
  onApply,
}: {
  show: boolean;
  onClose: () => void;
  documentPermissions: string[];
  onApply: (theme: ThemeSummary) => void;
}) {
  const { documentId } = useDocumentParams();
  const themes = useThemes(documentId);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const { mutate: renameTheme } = useRenameTheme();
  const [sortTab, setSortTab] = useState<string>('created');
  const [directions, setDirections] = useState<Record<string, string>>(SORT_DIRECTION_DEFAULTS);

  if (!themes.isSuccess) {
    return (
      <Modal show={show} onClose={onClose} size="xl">
        <Spinner />
      </Modal>
    );
  }

  const matching = themes.data.filter((theme) => theme.name.toLowerCase().includes(search.trim().toLowerCase()));
  const visible = sortThemes(matching, sortTab, directions[sortTab]);

  const pickTab = (tab: string) => {
    if (tab === sortTab) {
      setDirections({ ...directions, [tab]: directions[tab] === 'desc' ? 'asc' : 'desc' });
      return;
    }
    setSortTab(tab);
  };

  const selectedTheme = visible.find((theme) => theme.id === selectedId) ?? visible[0];

  return (
    <Modal
      titleLabelKey="theme:select-theme"
      show={show}
      onClose={onClose}
      size="xl"
      scrollable
      dialogClassName="modal-dialog--page-preview"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            {i18n('common:button.cancel')}
          </button>
          <button className="btn btn-primary" disabled={!selectedTheme} onClick={() => selectedTheme && onApply(selectedTheme)}>
            {i18n('theme:apply-theme')}
          </button>
        </>
      }
    >
      <div className="body d-flex flex-row gap-r-2" style={{ minHeight: 0 }}>
        <div className="d-flex flex-column gap-r-1" style={{ minWidth: '320px', maxWidth: '320px' }}>
          <input
            className="form-control form-control-sm"
            placeholder={i18n('common:search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={i18n('common:search')}
          />

          <ul className="nav nav-tabs nav-fill" role="tablist">
            {SORT_TABS.map((tab) => {
              const active = tab === sortTab;
              const ascending = directions[tab] === 'asc';
              return (
                <li className="nav-item" key={tab} role="presentation">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-label={`${i18nDynamic(`theme.sort.${tab}`)} — ${i18n(ascending ? 'theme:sort.ascending' : 'theme:sort.descending')}`}
                    className={`nav-link w-100 d-flex flex-row align-items-center justify-content-center gap-r-0-5 ${active ? 'active' : ''}`}
                    onClick={() => pickTab(tab)}
                  >
                    <span>{i18nDynamic(`theme.sort.${tab}`)}</span>
                    {active && <i className={`fal ${ascending ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>}
                  </button>
                </li>
              );
            })}
          </ul>

          <ul className="list-unstyled overflow-auto m-0 flex-grow-1" style={{ minHeight: 0 }}>
            {visible.map((theme) => {
              const isSelected = theme.id === selectedTheme?.id;
              const { canRename, canDelete, canPromote } = themeMenuPermissions(theme, documentPermissions);
              return (
                <li key={theme.id} className={`d-flex flex-row justify-content-between align-items-center rounded p-3 ${isSelected ? 'bg-blue-400' : ''}`}>
                  <Clickable
                    className="d-flex flex-column flex-grow-1 text-start"
                    onClick={() => (renamingId === theme.id ? undefined : setSelectedId(theme.id))}
                  >
                    {renamingId === theme.id ? (
                      <form
                        className="d-flex flex-row align-items-center gap-r-0-5"
                        onSubmit={(e) => {
                          e.preventDefault();
                          const name = renameValue.trim();
                          if (name && name !== theme.name) {
                            renameTheme({ themeId: theme.id, documentSuperId: documentId, name });
                          }
                          setRenamingId(null);
                        }}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={renameValue}
                          autoFocus
                          aria-label={i18n('theme:name')}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Escape' && setRenamingId(null)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button type="submit" className="btn-clear text-green-700 px-1" aria-label={i18n('common:button.save')}>
                          <i className="fal fa-check"></i>
                        </button>
                        <button
                          type="button"
                          className="btn-clear text-gray-700 px-1"
                          aria-label={i18n('common:button.cancel')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setRenamingId(null);
                          }}
                        >
                          <i className="fal fa-xmark"></i>
                        </button>
                      </form>
                    ) : (
                      <span>{theme.name}</span>
                    )}
                    <span className="d-flex flex-row align-items-center gap-r-1 mt-1">
                      <span className="text-muted fs-7">{`${i18n('theme:created-at')} ${formatDate(theme.createdAt)}`}</span>
                      <span className="ms-auto me-2">
                        <Level organization={theme.organization} />
                      </span>
                    </span>
                  </Clickable>
                  {(canRename || canDelete || canPromote) && (
                    <Tippy
                      content={
                        <ThemeMenu
                          theme={theme}
                          documentPermissions={documentPermissions}
                          onRename={(t) => {
                            setRenameValue(t.name);
                            setRenamingId(t.id);
                          }}
                          onDone={() => setOpenMenuId(null)}
                        />
                      }
                      interactive={true}
                      visible={openMenuId === theme.id}
                      onClickOutside={() => setOpenMenuId(null)}
                      placement="right-end"
                      theme="light"
                    >
                      <Clickable className="fs-3" onClick={() => setOpenMenuId(openMenuId === theme.id ? null : theme.id)}>
                        <i className="far fa-ellipsis-v"></i>
                      </Clickable>
                    </Tippy>
                  )}
                </li>
              );
            })}
            {visible.length === 0 && <li className="text-muted p-3">{i18n('theme:none-available')}</li>}
          </ul>
        </div>

        <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0, minHeight: 0 }}>
          <h3 className="fs-3 fw-medium m-0 mb-2">{i18n('theme:preview-title')}</h3>
          <ThemePreview withTheme={selectedTheme?.id} />
        </div>
      </div>
    </Modal>
  );
}

function themeMenuPermissions(theme: ThemeSummary, documentPermissions: string[]): ThemeMenuPerms {
  const system = isSystemTheme(theme);
  const canOwnThemes = documentPermissions.includes('DESIGN') || documentPermissions.includes('CODER');

  return {
    system,
    canRename: system ? authStore.hasGlobalPermission('TEMPLATE_UPDATE') : canOwnThemes,
    canDelete: system ? authStore.hasGlobalPermission('TEMPLATE_DELETE') : canOwnThemes,
    canPromote: authStore.hasGlobalPermission('TEMPLATE_UPDATE'),
  };
}

function ThemeMenu({
  theme,
  documentPermissions,
  onRename,
  onDone,
}: {
  theme: ThemeSummary;
  documentPermissions: string[];
  onRename: (theme: ThemeSummary) => void;
  onDone: () => void;
}) {
  const { documentId } = useDocumentParams();
  const { mutate: deleteTheme } = useDeleteTheme();
  const { mutate: promoteTheme } = usePromoteTheme();
  const { mutate: demoteTheme } = useDemoteTheme();

  const { system, canRename, canDelete, canPromote } = themeMenuPermissions(theme, documentPermissions);

  const run = (action: () => void) => () => {
    action();
    onDone();
  };

  return (
    <div className="d-flex flex-column bg-white border border-line rounded shadow-sm py-1">
      {canRename && (
        <Clickable className="p-2 text-start" onClick={run(() => onRename(theme))}>
          <i className="fal fa-pen me-2"></i>
          {i18n('theme:change-name')}
        </Clickable>
      )}
      {canPromote &&
        (system ? (
          <Clickable className="p-2 text-start" onClick={run(() => demoteTheme({ themeId: theme.id, documentSuperId: documentId }))}>
            <i className="fal fa-unlock me-2"></i>
            {i18n('theme:demote')}
          </Clickable>
        ) : (
          <Clickable className="p-2 text-start" onClick={run(() => promoteTheme({ themeId: theme.id }))}>
            <i className="fal fa-lock me-2"></i>
            {i18n('theme:promote')}
          </Clickable>
        ))}
      {canDelete && (
        <Clickable className="p-2 text-start" onClick={run(() => deleteTheme({ themeId: theme.id, documentSuperId: documentId }))}>
          <i className="fal fa-trash me-2"></i>
          {i18n('theme:delete')}
        </Clickable>
      )}
    </div>
  );
}
